import { useState, useEffect, useCallback } from 'react';
import { Collapse } from '@mui/material';
import { CortexDialog, CortexDialogBody } from '../dialogs/CortexDialog';
import {
  Eye as ViewIcon,
  XCircle as CancelIcon,
  ChevronDown as ExpandMoreIcon,
  ChevronUp as ExpandLessIcon,
  RefreshCw as RefreshIcon,
} from 'lucide-react';
import { Button, Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import type { ChipColor } from '@coordio/ui';
import { assistantsApiService } from '../../services/assistantsApiService';
import { sessionService } from '../../services/sessionService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import type { AgentSessionDto } from '../../types/session';
import type { RunObject, RunStepObject } from '../../types/assistantsApi';

interface Props {
  agentId: string;
  agentSlug: string;
}

const STATUS_COLORS: Record<string, ChipColor> = {
  completed: 'green',
  in_progress: 'blue',
  queued: 'blue',
  requires_action: 'amber',
  failed: 'red',
  cancelled: 'default',
  cancelling: 'default',
  expired: 'red',
};

export const RunMonitor = ({ agentId, agentSlug }: Props) => {
  const [threads, setThreads] = useState<AgentSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedThread, setExpandedThread] = useState<string | null>(null);
  const [runs, setRuns] = useState<Record<string, RunObject[]>>({});
  const [runsLoading, setRunsLoading] = useState<Record<string, boolean>>({});
  const [selectedRun, setSelectedRun] = useState<RunObject | null>(null);
  const [steps, setSteps] = useState<RunStepObject[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);

  useEffect(() => {
    loadThreads();
  }, [agentId]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const result = await sessionService.getSessions({
        agentDefinitionId: agentId,
        pageSize: 100,
        searchTerm: 'Thread',
      });
      setThreads(result.items ?? []);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRuns = useCallback(async (threadId: string) => {
    const threadIdFormatted = `thread_${threadId.replace(/-/g, '')}`;
    setRunsLoading((prev) => ({ ...prev, [threadId]: true }));
    try {
      const result = await assistantsApiService.listRuns(agentSlug, threadIdFormatted);
      setRuns((prev) => ({ ...prev, [threadId]: result.data ?? [] }));
    } catch {
      setRuns((prev) => ({ ...prev, [threadId]: [] }));
    } finally {
      setRunsLoading((prev) => ({ ...prev, [threadId]: false }));
    }
  }, [agentSlug]);

  const toggleThread = (threadId: string) => {
    if (expandedThread === threadId) {
      setExpandedThread(null);
    } else {
      setExpandedThread(threadId);
      if (!runs[threadId]) {
        loadRuns(threadId);
      }
    }
  };

  const viewSteps = async (run: RunObject) => {
    setSelectedRun(run);
    setStepsLoading(true);
    try {
      const result = await assistantsApiService.listRunSteps(agentSlug, run.thread_id, run.id);
      setSteps(result.data ?? []);
    } catch {
      setSteps([]);
    } finally {
      setStepsLoading(false);
    }
  };

  const handleCancel = async (run: RunObject) => {
    try {
      await assistantsApiService.cancelRun(agentSlug, run.thread_id, run.id);
      const threadGuid = run.thread_id.replace('thread_', '');
      loadRuns(threadGuid);
    } catch (err) {
      console.error('Failed to cancel run', err);
    }
  };

  const runColumns: DataTableColumn<RunObject>[] = [
    { id: 'id', label: 'Run ID', render: (r) => <span className="font-mono text-xs">{r.id}</span> },
    {
      id: 'status', label: 'Status',
      render: (r) => <Chip label={r.status} color={STATUS_COLORS[r.status] ?? 'default'} variant="outlined" />,
    },
    { id: 'model', label: 'Model', render: (r) => r.model },
    {
      id: 'tokens', label: 'Tokens',
      render: (r) => r.usage ? `${r.usage.prompt_tokens} / ${r.usage.completion_tokens}` : '—',
    },
    {
      id: 'created', label: 'Created',
      render: (r) => new Date(r.created_at * 1000).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <div className="flex gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={() => viewSteps(r)}><ViewIcon size={16} /></Button>
              </TooltipTrigger>
              <TooltipContent>View steps</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {(r.status === 'queued' || r.status === 'in_progress' || r.status === 'requires_action') && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => handleCancel(r)}><CancelIcon size={16} /></Button>
                </TooltipTrigger>
                <TooltipContent>Cancel</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[var(--mui-palette-text-secondary)]">
          Monitor runs across threads. Expand a thread to see its runs.
        </p>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="icon" onClick={loadThreads}><RefreshIcon size={16} /></Button>
            </TooltipTrigger>
            <TooltipContent>Refresh</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {loading ? (
        <p>Loading threads...</p>
      ) : threads.length === 0 ? (
        <p className="text-[var(--mui-palette-text-secondary)]">No threads found for this agent.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {threads.map((t) => (
            <div
              key={t.id}
              className="border border-[var(--glass-border)] rounded-lg overflow-hidden"
            >
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[var(--glass-bg-subtle)]"
                onClick={() => toggleThread(t.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedThread === t.id ? <ExpandLessIcon size={16} /> : <ExpandMoreIcon size={16} />}
                  <span className="text-sm font-medium">{t.sessionName}</span>
                  <Chip label={`${t.messageCount ?? 0} msgs`} variant="outlined" />
                </div>
                <span className="text-xs text-[var(--mui-palette-text-secondary)]">
                  {new Date(t.createdAt).toLocaleString()}
                </span>
              </div>

              <Collapse in={expandedThread === t.id}>
                <div className="px-4 pb-4">
                  <DataTable
                    columns={runColumns}
                    rows={runs[t.id] ?? []}
                    loading={runsLoading[t.id] ?? false}
                    emptyMessage="No runs for this thread."
                    size="small"
                  />
                </div>
              </Collapse>
            </div>
          ))}
        </div>
      )}

      <CortexDialog open={!!selectedRun} onClose={() => setSelectedRun(null)} title={<>Run Steps — {selectedRun?.id}</>} size="md" data-testid="run-steps-dialog">
        <CortexDialogBody>
          {stepsLoading ? (
            <p>Loading steps...</p>
          ) : steps.length === 0 ? (
            <p className="text-[var(--mui-palette-text-secondary)]">No steps recorded.</p>
          ) : (
            <div className="flex flex-col gap-3 mt-2">
              {steps.map((s) => (
                <div
                  key={s.id}
                  className="p-4 rounded-lg border border-[var(--glass-border)]"
                  style={{
                    background: s.type === 'tool_calls' ? 'rgba(255, 152, 0, 0.04)' : 'var(--glass-bg-subtle)',
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex gap-2 items-center">
                      <Chip label={s.type} variant="outlined" />
                      <Chip label={s.status} color={STATUS_COLORS[s.status] ?? 'default'} variant="outlined" />
                    </div>
                    {s.usage && (
                      <span className="text-xs text-[var(--mui-palette-text-secondary)]">
                        {s.usage.prompt_tokens} + {s.usage.completion_tokens} tokens
                      </span>
                    )}
                  </div>
                  <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                    {JSON.stringify(s.step_details, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </CortexDialogBody>
      </CortexDialog>
    </div>
  );
};
